import { defineStore } from "pinia";
import { ref } from "vue";
import socketService from "../services/socket";

export const useSocketStore = defineStore("socket", () => {
  // State
  const isConnected = ref(false);
  const connectionError = ref(null);
  const currentEventId = ref(null);

  // Actions
  const connect = (token = null) => {
    try {
      const socket = socketService.connect(token);

      socket.on("connect", () => {
        isConnected.value = true;
        connectionError.value = null;
        console.log("✅ Socket connected in store");

        // Re-join event room if we were previously in one
        if (currentEventId.value) {
          console.log(`🔄 Re-joining event room: event_${currentEventId.value}`);
          socketService.emit("join_event", { eventId: currentEventId.value });
        }
      });

      socket.on("disconnect", (reason) => {
        isConnected.value = false;
        console.log("⚠️ Socket disconnected in store:", reason);
      });

      socket.on("connect_error", (error) => {
        connectionError.value = error.message;
        isConnected.value = false;
        console.error("❌ Socket connection error in store:", error.message);
      });

      socket.on("reconnect", (attemptNumber) => {
        console.log(`✅ Socket reconnected in store after ${attemptNumber} attempts`);
        isConnected.value = true;
        connectionError.value = null;
      });

      return socket;
    } catch (error) {
      connectionError.value = error.message;
      throw error;
    }
  };

  const disconnect = () => {
    socketService.disconnect();
    isConnected.value = false;
    connectionError.value = null;
  };

  const emit = (event, data) => {
    socketService.emit(event, data);
  };

  const on = (event, callback) => {
    socketService.on(event, callback);
  };

  const off = (event, callback) => {
    socketService.off(event, callback);
  };

  const getSocket = () => {
    return socketService.getSocket();
  };

  const joinEvent = (eventId) => {
    currentEventId.value = eventId;
    const socket = socketService.getSocket();
    if (socket && socket.connected) {
      socketService.emit("join_event", { eventId });
      console.log(`📍 Joined event room: event_${eventId}`);
    } else if (socket) {
      // If socket is not connected, wait for connection
      socket.once("connect", () => {
        socketService.emit("join_event", { eventId });
        console.log(`📍 Joined event room after connect: event_${eventId}`);
      });
    } else {
      // Socket doesn't exist, connect first
      const newSocket = connect();
      if (newSocket) {
        newSocket.once("connect", () => {
          socketService.emit("join_event", { eventId });
          console.log(`📍 Joined event room after new connection: event_${eventId}`);
        });
      }
    }
  };

  const leaveEvent = (eventId) => {
    const socket = socketService.getSocket();
    if (socket && socket.connected) {
      socketService.emit("leave_event", { eventId });
    }
    if (currentEventId.value === eventId) {
      currentEventId.value = null;
    }
  };

  return {
    // State
    isConnected,
    connectionError,
    currentEventId,

    // Actions
    connect,
    disconnect,
    emit,
    on,
    off,
    getSocket,
    joinEvent,
    leaveEvent
  };
});
